import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Column, TwoColGrid, Spacer } from "../../../components/Layout/Layout";
import { validateEmailIfExists } from "../../../core/helpers/validation";
import Autocomplete from "../../../ui-lib/components/Autocomplete/Autocomplete";
import useTranslations from "../../../core/i18n/useTranslations";
import useUser from "../../../core/user/useUser";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import Checkbox from "../../../ui-lib/components/Checkbox/Checkbox";
import TextArea from "../../../ui-lib/components/Inputs/Textarea";
import useSharedObject from "../SharedObjectContext/useSharedObject";

import styles from "./ObjectGeneral.module.css";

const ObjectGeneralPersonalDetails = () => {
  const t = useTranslations();
  const { config } = useUser();
  const { objectFields } = useSharedObject();

  const {
    formState: { errors },
    control,
  } = useFormContext();

  const hasTrygghetsjourenJourledRole = useMemo(
    () => config?.show.includes("ShowObjectDetailsVariant1"),
    [],
  );

  return (
    <div className={styles.ObjectBasicContainer}>
      {!config?.show.includes("CareiumHideFields") && (
        <>
          <h2>{t("Objects:general_personal_details_title")}</h2>
          <p>{t("Objects:general_personal_details_description")}</p>

          <Spacer size={16} />

          {!hasTrygghetsjourenJourledRole && (
            <>
              <TwoColGrid>
                <Autocomplete
                  fieldData={objectFields?.title}
                  name="title"
                  defaultPlaceholder={t("Common:placeholder_object_title")}
                  defaultLabel={t("Common:labels_title")}
                />
                <Autocomplete
                  fieldData={objectFields?.profession}
                  name="profession"
                  defaultPlaceholder={t("Common:placeholder_profession")}
                  defaultLabel={t("Common:labels_profession")}
                />
              </TwoColGrid>

              <Spacer size={16} />
            </>
          )}

          <TwoColGrid>
            <Controller
              name="email"
              control={control}
              rules={{
                maxLength: 60,
                validate: validateEmailIfExists,
              }}
              render={({ field }) => (
                <TextInput
                  label={t("Common:email")}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("Common:placeholder_email")}
                  validationError={
                    errors?.email && t("Errors:input_email_format")
                  }
                />
              )}
            />
            <Controller
              name="nationalIdNumber"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Common:labels_id_number")}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase());
                  }}
                />
              )}
            />
          </TwoColGrid>
          <Spacer size={16} />
        </>
      )}

      <TwoColGrid>
        <Column>
          {!hasTrygghetsjourenJourledRole && (
            <Controller
              name="chiNumber"
              rules={{
                maxLength: 10,
                validate: (val) =>
                  val?.length ? /^[a-zA-Z0-9]+$/.test(val) : true,
              }}
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Objects:labels_chi_number")}
                  placeholder={t("Objects:placeholder_chi_number")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.chiNumber &&
                    (errors.chiNumber.type === "maxLength"
                      ? t("Errors:max_chars_exceeded").replace("{0}", "10")
                      : t("Errors:input_alphanumeric"))
                  }
                />
              )}
            />
          )}
        </Column>
        <Column>
          {!hasTrygghetsjourenJourledRole && (
            <Controller
              name="externalId"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Common:labels_external_id")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Column>
      </TwoColGrid>
      <TwoColGrid>
        <Column align="start" className="mt-24">
          {!config?.show.includes("CareiumHideFields") && (
            <Controller
              name="protectedIdentidy"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={field.onChange}
                  style={{ padding: 0 }}
                  label={t("Common:labels_protected_identity")}
                />
              )}
            />
          )}
        </Column>
      </TwoColGrid>
      {!config?.show.includes("CareiumHideFields") && (
        <>
          <Spacer size={16} />
          {objectFields?.additionalInfo ? (
            <Autocomplete
              fieldData={objectFields.additionalInfo}
              name="additionalInfo"
              defaultLabel={t("Common:labels_additional_info")}
            />
          ) : (
            <Controller
              name="additionalInfo"
              control={control}
              rules={{ maxLength: 2500 }}
              render={({ field }) => (
                <TextArea
                  label={t("Common:labels_additional_info")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors.additionalInfo &&
                    t("Errors:max_chars_exceeded").replace("{0}", "2500")
                  }
                />
              )}
            />
          )}

          <Spacer size={16} />
          {objectFields?.userCustomerSpecific3 ? (
            <Autocomplete
              fieldData={objectFields?.userCustomerSpecific3}
              name="userCustomerSpecific3"
              defaultLabel={t("Objects:known_health_conditions_label")}
            />
          ) : (
            <Controller
              name="userCustomerSpecific3"
              rules={{ maxLength: 2000 }}
              control={control}
              render={({ field }) => (
                <TextArea
                  label={t("Objects:known_health_conditions_label")}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.userCustomerSpecific3 &&
                    t("Errors:max_chars_exceeded").replace("{0}", "2000")
                  }
                />
              )}
            />
          )}

          <Spacer size={16} />
          {objectFields?.medicalCondition ? (
            <Autocomplete
              fieldData={objectFields.medicalCondition}
              name="medicalCondition"
              defaultLabel={t("Common:labels_condition")}
            />
          ) : (
            <Controller
              name="medicalCondition"
              rules={{ maxLength: 1000 }}
              control={control}
              render={({ field }) => (
                <TextArea
                  label={t("Common:labels_condition")}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.medicalCondition &&
                    t("Errors:max_chars_exceeded").replace("{0}", "1000")
                  }
                />
              )}
            />
          )}

          <Spacer size={16} />

          {objectFields?.medication ? (
            <Autocomplete
              fieldData={objectFields.medication}
              name="medication"
              defaultLabel={t("Common:labels_medication")}
            />
          ) : (
            <Controller
              name="medication"
              rules={{ maxLength: 1000 }}
              control={control}
              render={({ field }) => (
                <TextArea
                  label={t("Common:labels_medication")}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.medication &&
                    t("Errors:max_chars_exceeded").replace("{0}", "1000")
                  }
                />
              )}
            />
          )}

          <Spacer size={16} />
          <Controller
            name="livingWithOthers"
            control={control}
            render={({ field }) => (
              <Checkbox
                className="p-0"
                checked={field.value}
                onChange={field.onChange}
                label={t("Objects:livingWithOthers_label")}
              />
            )}
          />
        </>
      )}
      {!hasTrygghetsjourenJourledRole &&
        !config?.show.includes("CareiumHideFields") && (
          <>
            <Spacer size={16} />

            <Controller
              name="carePlan"
              rules={{ maxLength: 1000 }}
              control={control}
              render={({ field }) => (
                <TextArea
                  label={t("Common:labels_care_plan")}
                  inputRef={field.ref}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors?.carePlan &&
                    t("Errors:max_chars_exceeded").replace("{0}", "1000")
                  }
                />
              )}
            />
          </>
        )}

      {!hasTrygghetsjourenJourledRole &&
        !config?.show.includes("CareiumHideFields") && (
          <>
            <Spacer size={16} />
            <Autocomplete
              fieldData={objectFields?.supportingAids}
              name="supportingAids"
              defaultLabel={t("Common:labels_supporting_aids")}
            />
          </>
        )}
      <Spacer size={16} />
      <Autocomplete
        fieldData={objectFields?.userCustomerSpecific1}
        name="userCustomerSpecific1"
        defaultLabel={t("Objects:userCustomerSpecific1_label")}
      />
      <Spacer size={16} />
      <Autocomplete
        fieldData={objectFields?.userCustomerSpecific2}
        name="userCustomerSpecific2"
        defaultLabel={t("Objects:userCustomerSpecific2_label")}
      />
      <Spacer size={16} />
      <TwoColGrid>
        <Autocomplete
          fieldData={objectFields?.caringResponsibilities}
          name="caringResponsibilities"
          defaultLabel={t("Objects:caringResponsibilities_label")}
          defaultValidation={{ maxLength: 300 }}
        />

        <Autocomplete
          fieldData={objectFields?.powerOfAttorney}
          name="powerOfAttorney"
          defaultLabel={t("Objects:powerOfAttorney_label")}
          defaultValidation={{ maxLength: 300 }}
        />
      </TwoColGrid>
      <Spacer size={16} />
      <Controller
        name="inReceiptOfHomeCare"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            onChange={field.onChange}
            style={{ padding: 0 }}
            label={t("Objects:inReceiptOfHomeCare_label")}
          />
        )}
      />
    </div>
  );
};

export default ObjectGeneralPersonalDetails;
